import { flow } from 'lodash'
import ContentCopyIcon from 'mdi-react/ContentCopyIcon'
import React, { useMemo } from 'react'
import { Column } from 'react-table'
import { insertIf } from 'utils-next/arrays'

import { Button, ButtonGroup, Chip, useMediaQuery, useTheme } from '@mui/material'

import { history } from '../../../../../../duck/store'
import { User } from '../../../../../../entities-next/user'
import { getCorporateDetailsPath } from '../../../CorporateManager'

export function useUserDataRows(extraRows: Column<User>[]): Column<User>[] {
  const theme = useTheme()
  const mediumScreenSize = useMediaQuery(theme.breakpoints.up('sm'))
  const largeScreenSize = useMediaQuery(theme.breakpoints.up('lg'))
  return useMemo(
    () => [
      ...baseUserRowData(),
      ...insertIf(largeScreenSize, extraUserRowData()),
      ...insertIf(mediumScreenSize, userGroupRowData),
      ...extraRows,
    ],
    [theme, extraRows, largeScreenSize, mediumScreenSize]
  ) as any
}

const baseUserRowData = () => [
  {
    Header: 'Id',
    accessor: getUserId,
    maxWidth: 64,
  },
  {
    Header: 'Name',
    accessor: flow(getUserName, getStringOrPlaceholder),
  },
]

const userGroupRowData = [
  {
    Header: 'Corporate',
    accessor: getCorporateId,
    maxWidth: 64,
  },
]

const extraUserRowData = () => [
  {
    Header: 'Email',
    accessor: flow(({ email }) => email, getStringOrPlaceholder),
    minWidth: 200,
  },
  {
    Header: 'Role',
    accessor: flow(getUserRole, getStringOrPlaceholder),
    maxWidth: 128,
  },
  {
    Header: 'Status',
    accessor: getUserStatus,
    minWidth: 200,
  },
]
const getUserStatus = ({ status }: User) => {
  let color: 'default' | 'success' | 'error' | 'warning' | 'info' = 'default'

  if (status === 'active') {
    color = 'success'
  } else if (status === 'churned' || status === 'zombie') {
    color = 'error'
  } else if (status === 'on-hold') {
    color = 'warning'
  }

  return <Chip color={color} label={status}></Chip>
}
function getStringOrPlaceholder(value?: string) {
  return value || '-'
}

function getUserId({ id }: User) {
  const handleCopy = (e: React.MouseEvent<HTMLButtonElement>) => {
    navigator.clipboard.writeText(id.toString())
    e.stopPropagation()
  }
  return (
    <ButtonGroup variant="contained" aria-label="outlined primary button group">
      <Button variant="contained" size="small" color="info" title="Go to details">
        {id}
      </Button>
      <Button color="info" size="small" onClick={handleCopy} title="Copy id to clipboard">
        <ContentCopyIcon size={16} />
      </Button>
    </ButtonGroup>
  )
}

function getUserRole({ role }: User) {
  return role.charAt(0).toUpperCase() + role.slice(1)
}

function getCorporateId({ corporateId }: User) {
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    history.push(getCorporateDetailsPath(corporateId))
    e.stopPropagation()
  }
  const handleCopy = (e: React.MouseEvent<HTMLButtonElement>) => {
    navigator.clipboard.writeText(corporateId.toString())
    e.stopPropagation()
  }

  return (
    <ButtonGroup variant="contained" aria-label="outlined primary button group">
      <Button variant="contained" size="small" color="info" onClick={handleClick} title="Go to corporate details">
        {corporateId}
      </Button>
      <Button color="info" size="small" onClick={handleCopy} title="Copy corporate id to clipboard">
        <ContentCopyIcon size={16} />
      </Button>
    </ButtonGroup>
  )
}

function getUserName({ firstName, lastName }: User) {
  if (!firstName) {
    return getStringOrPlaceholder()
  } else {
    return `${firstName} ${lastName}`
  }
}
